import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, User } from "lucide-react";
import { api } from "@/lib/api";
import { ssgData } from "@/lib/ssg";
import useSEO from "@/hooks/useSEO";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(() => ssgData(`blogPost:${slug}`) || null);
  const [related, setRelated] = useState(() => ssgData("blogPosts") || []);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: post?.title || "Loading article",
    description: post?.excerpt || "",
    image: post?.cover_image,
    type: "article",
  });

  useEffect(() => {
    api
      .get(`/blog/posts/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true));
    api.get(`/blog/posts`).then(({ data }) => setRelated(data));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blog-post-not-found">
        <h1 className="font-display text-4xl text-[#2D2623]">Article not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-[#E07A5F]"><ArrowLeft className="w-4 h-4"/> Back to journal</Link>
      </div>
    );
  }

  if (!post) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-[#A39171]">Loading…</div>;
  }

  const more = related.filter((r) => r.slug !== post.slug).slice(0, 3);

  return (
    <div data-testid="blog-post-page">
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-10">
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-[#5C524E] hover:text-[#E07A5F] text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to journal
        </Link>
        <p className="eyebrow text-[#A39171] mt-8 mb-5">{post.category}</p>
        <h1 className="font-display text-4xl md:text-6xl text-[#2D2623] leading-[1.05] tracking-tight">
          {post.title}
        </h1>
        <div className="mt-8 flex items-center gap-6 text-sm text-[#5C524E]">
          <span className="flex items-center gap-2"><User className="w-4 h-4 text-[#E07A5F]" /> {post.author}</span>
          <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#E07A5F]" />
            {new Date(post.created_at).toLocaleDateString("en-IN", { month: "long", day: "numeric", year: "numeric" })}
          </span>
        </div>
      </section>

      {post.cover_image && (
        <section className="max-w-5xl mx-auto px-6 md:px-10 mb-12">
          <div className="rounded-3xl overflow-hidden aspect-[16/9]">
            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <p className="text-xl text-[#2D2623] font-display leading-snug border-l-2 border-[#E07A5F] pl-5 italic">
          {post.excerpt}
        </p>
        <div className="mt-10 text-[#2D2623]/90 text-lg leading-[1.85] whitespace-pre-line">
          {post.content}
        </div>
      </section>

      {more.length > 0 && (
        <section className="bg-[#F4EFE6]/60 py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <h2 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight mb-10">More from the journal</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {more.map((p, i) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-blog-${i}`}
                  className="group bg-[#FDFBF7] rounded-3xl overflow-hidden border border-[#A39171]/15 hover:shadow-[0_20px_50px_rgba(163,145,113,0.15)] transition-all hover:-translate-y-1"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <p className="eyebrow text-[#A39171] mb-2">{p.category}</p>
                    <h3 className="font-display text-xl text-[#2D2623] leading-tight">{p.title}</h3>
                    <span className="mt-4 inline-flex items-center gap-1 text-xs text-[#2D2623] font-medium">
                      Read <ArrowUpRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
